import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Bot,
  Sparkles,
  Zap,
  Send,
  History,
  Settings,
  MoreVertical,
  Activity,
  Shield,
  Briefcase,
  BarChart3,
  Home,
  Landmark,
  Boxes,
  TrendingDown,
  Layers,
  Cpu,
  Workflow,
  User,
  // Retail agent icons
  Coins,
  FileSpreadsheet,
  Scale,
  Building2,
  ClipboardList,
  ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { sanitizeHtml } from '../utils/sanitize';
import {
  CollapsedRail,
  CollapsedRailItem,
  CollapsedRailSection,
  WorkspaceRailToggle,
} from './shared/Workspace';

// ============== Markdown rendering (GFM subset: headings, lists, tables, bold, code, links) ==============
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inlineMd = (s: string) =>
  escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

function mdToHtml(md: string): string {
  if (!md) return '';
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (trimmed === '') { closeList(); i++; continue; }

    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) { closeList(); const lvl = heading[1].length; out.push(`<h${lvl}>${inlineMd(heading[2])}</h${lvl}>`); i++; continue; }

    // GFM table: row with pipes followed by a separator row (---|---)
    if (trimmed.includes('|') && i + 1 < lines.length
        && /^\s*\|?[\s:|-]*-[\s:|-]*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      closeList();
      const parseRow = (r: string) => r.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const headers = parseRow(trimmed);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') { body.push(parseRow(lines[i])); i++; }
      out.push('<table><thead><tr>' + headers.map(h => `<th>${inlineMd(h)}</th>`).join('') + '</tr></thead><tbody>'
        + body.map(r => '<tr>' + r.map(c => `<td>${inlineMd(c)}</td>`).join('') + '</tr>').join('')
        + '</tbody></table>');
      continue;
    }

    const ulm = /^[-*]\s+(.*)$/.exec(trimmed);
    if (ulm) { if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul'; } out.push(`<li>${inlineMd(ulm[1])}</li>`); i++; continue; }
    const olm = /^\d+\.\s+(.*)$/.exec(trimmed);
    if (olm) { if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol'; } out.push(`<li>${inlineMd(olm[1])}</li>`); i++; continue; }

    closeList();
    out.push(`<p>${inlineMd(trimmed)}</p>`);
    i++;
  }
  closeList();
  return sanitizeHtml(out.join('\n'));
}

// ============== Shared Components ==============
const Container = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  background: transparent;
`;

const Sidebar = styled.div<{ $collapsed?: boolean }>`
  width: ${p => p.$collapsed ? '64px' : '360px'};
  min-width: ${p => p.$collapsed ? '64px' : '360px'};
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, min-width 0.18s ease;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  position: relative;
`;

const MarkdownBody = styled.div`
  font-size: 0.875rem;
  line-height: 1.6;
  word-break: break-word;
  h1, h2, h3, h4 { font-weight: 700; color: #fff; margin: 0.7rem 0 0.35rem; line-height: 1.3; }
  h1 { font-size: 1.05rem; } h2 { font-size: 1rem; } h3 { font-size: 0.95rem; } h4 { font-size: 0.9rem; }
  p { margin: 0.4rem 0; }
  strong { color: #fff; font-weight: 700; }
  ul, ol { padding-left: 1.25rem; margin: 0.35rem 0; }
  li { margin: 0.15rem 0; }
  ul > li { list-style: disc; }
  ol > li { list-style: decimal; }
  code { background: rgba(255,255,255,0.08); padding: 0.05rem 0.3rem; border-radius: 4px; font-size: 0.82em; }
  a { color: #93c5fd; text-decoration: underline; }
  table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 0.82rem; }
  th, td { border: 1px solid rgba(255,255,255,0.12); padding: 0.3rem 0.5rem; text-align: left; }
  th { background: rgba(255,255,255,0.05); font-weight: 600; color: #fff; }
  & > *:first-child { margin-top: 0; }
  & > *:last-child { margin-bottom: 0; }
`;

const AIAgentsHub: React.FC = () => {
  const { user } = useAuth();
  const { isRetail } = useTenantConfig();
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAmendments, setPendingAmendments] = useState<any[]>([]);
  const [amendmentBusy, setAmendmentBusy] = useState<number | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Institutional agents
  const institutionalAgents = [
    {
      id: 'intent-analyst',
      name: 'Intent Analyst',
      role: 'Universal Agent',
      desc: 'Ask anything — automatically routes to the right specialist agent.',
      endpoint: '/api/agents/ask',
      icon: <Sparkles className="w-5 h-5" />,
      color: 'blue',
      isOrchestrator: true,
      hints: [
        "Analyze my portfolio for concentration risk",
        "Show me all open reconciliation breaks",
        "Recommend a trade to reduce duration",
        "What compliance rules apply to portfolio #9?"
      ]
    },
    {
      id: 'risk-sentinel',
      name: 'Risk Sentinel',
      role: 'Compliance',
      desc: 'Direct access to compliance rules, violations, and trade validation.',
      endpoint: '/api/agents/compliance/ask',
      icon: <Shield className="w-5 h-5" />,
      color: 'indigo',
      hints: [
        "What are the active compliance rules?",
        "Validate a $5M buy of AAPL in portfolio #1",
        "Check portfolio #9 for violations",
        "Explain the single issuer concentration rule"
      ]
    },
    {
      id: 'liquidity-patrol',
      name: 'Liquidity Patrol',
      role: 'Research',
      desc: 'Direct access to security research, portfolio analysis, and trade ideas.',
      endpoint: '/api/agents/research/ask',
      icon: <Activity className="w-5 h-5" />,
      color: 'emerald',
      hints: [
        "Search for corporate bonds maturing in 3-5 years",
        "Analyze portfolio #1 holdings and allocation",
        "Find Treasury securities with high yield",
        "Recommend a trade for yield enhancement"
      ]
    },
    {
      id: 'workflow-automator',
      name: 'Workflow Automator',
      role: 'Reconciliation',
      desc: 'Direct access to reconciliation breaks, patterns, and resolution.',
      endpoint: '/api/agents/reconciliation/ask',
      icon: <Workflow className="w-5 h-5" />,
      color: 'violet',
      hints: [
        "Show me all open reconciliation breaks",
        "Analyze break #101 and suggest resolution",
        "What patterns do you see in recent breaks?",
        "Show breaks by type"
      ]
    }
  ];

  // Retail/RIA agents - specialized for wealth management workflows
  const retailAgents = [
    {
      id: 'advisor-copilot',
      name: 'Advisor Copilot',
      role: 'Universal Agent',
      desc: 'Ask anything — automatically routes to specialized RIA agents.',
      endpoint: '/api/agents/ask',
      icon: <Sparkles className="w-5 h-5" />,
      color: 'blue',
      isOrchestrator: true,
      hints: [
        "Analyze the Smith Family portfolio drift",
        "Find tax-loss harvesting opportunities",
        "Show K-1 documents pending review",
        "Check ADV compliance status"
      ]
    },
    {
      id: 'tax-alpha-agent',
      name: 'Tax Alpha Agent',
      role: 'Tax Optimization',
      desc: 'Tax-loss harvesting, wash sale prevention, and gain/loss optimization.',
      endpoint: '/api/agents/tax-alpha/ask',
      icon: <Coins className="w-5 h-5" />,
      color: 'emerald',
      hints: [
        "Find tax-loss harvesting opportunities",
        "Check for wash sale violations",
        "Optimize gains across all accounts",
        "Show unrealized losses over $10K"
      ]
    },
    {
      id: 'k1-accounting-agent',
      name: 'K-1 Processor',
      role: 'Alternative Accounting',
      desc: 'K-1 document processing, partnership allocations, and tax basis tracking.',
      endpoint: '/api/agents/k1-accounting/ask',
      icon: <FileSpreadsheet className="w-5 h-5" />,
      color: 'amber',
      hints: [
        "Show all pending K-1 documents",
        "Process Q4 partnership allocations",
        "What's the tax basis for Chen Family alts?",
        "Reconcile K-1 vs. capital account"
      ]
    },
    {
      id: 'alts-execution-agent',
      name: 'Alts Executor',
      role: 'Alternative Investments',
      desc: 'Private fund subscriptions, capital calls, distributions, and liquidity.',
      endpoint: '/api/agents/alts-execution/ask',
      icon: <Building2 className="w-5 h-5" />,
      color: 'violet',
      hints: [
        "Show upcoming capital calls",
        "Process pending distributions",
        "Check liquidity for Johnson Trust alts",
        "Submit subscription for Blackstone fund"
      ]
    },
    {
      id: 'governance-agent',
      name: 'Compliance Guardian',
      role: 'RIA Governance',
      desc: 'ADV filing, best execution, fiduciary duties, and regulatory compliance.',
      endpoint: '/api/agents/governance/ask',
      icon: <Scale className="w-5 h-5" />,
      color: 'rose',
      hints: [
        "Check Form ADV filing status",
        "Review best execution records",
        "Any fiduciary duty concerns?",
        "Audit trail for Smith Family trades"
      ]
    }
  ];

  // Alternatives (Private Credit) agents — institutional, asset-class specific
  const alternativesAgents = [
    {
      id: 'private-credit-analyst',
      name: 'Private Credit Analyst',
      role: 'Private Credit',
      desc: 'Direct lending book — loans, covenant headroom, drawdowns/DDTL, and borrower watchlist.',
      endpoint: '/api/agents/private-credit/ask',
      icon: <Building2 className="w-5 h-5" />,
      color: 'amber',
      hints: [
        "List private credit loans by borrower",
        "Show covenant breaches and warnings",
        "What's the unfunded and DDTL undrawn balance?",
        "Show the borrower deterioration watchlist"
      ]
    },
    {
      id: 'real-estate-analyst',
      name: 'Real Estate Analyst',
      role: 'Real Estate',
      desc: 'Property equity and RE debt — NOI, cap rate, occupancy, valuations, LTV/DSCR, maturities.',
      endpoint: '/api/agents/real-estate/ask',
      icon: <Home className="w-5 h-5" />,
      color: 'teal',
      hints: [
        "List real estate equity holdings and their cap rates",
        "Show real estate loans and current LTV/DSCR",
        "Which properties have the lowest occupancy?",
        "Show construction loans and unfunded commitments"
      ]
    },
    {
      id: 'private-equity-analyst',
      name: 'Private Equity Analyst',
      role: 'Private Equity',
      desc: 'Fund commitments, capital calls, distributions, and J-curve economics (called/distributed/unfunded).',
      endpoint: '/api/agents/private-equity/ask',
      icon: <Coins className="w-5 h-5" />,
      color: 'fuchsia',
      hints: [
        "List our private fund commitments",
        "Show any overdue capital calls",
        "What's our total unfunded commitment?",
        "Show recent distributions"
      ]
    }
  ];

  // Credit & Loans (traded credit) agents — institutional, asset-class specific
  const creditLoansAgents = [
    {
      id: 'abf-facility-analyst',
      name: 'ABF Facility Analyst',
      role: 'Asset-Based Finance',
      desc: 'Borrowing-base facilities — availability, servicer tapes, PDF-vs-tape checks, advance-rate tests, and lifecycle events.',
      endpoint: '/api/agents/abf/ask',
      icon: <Briefcase className="w-5 h-5" />,
      color: 'cyan',
      hints: [
        "Which ABF facilities have the tightest availability?",
        "Explain Bayview borrowing-base movement",
        "Show ABF facilities with advance-rate exceptions",
        "What changed after the latest servicer tape?"
      ]
    },
    {
      id: 'leveraged-loan-analyst',
      name: 'Leveraged Loan Analyst',
      role: 'Leveraged Loans',
      desc: 'Syndicated / broadly-syndicated loans — commitment, all-in rate, spread, ratings, maturity wall.',
      endpoint: '/api/agents/leveraged-loan/ask',
      icon: <Landmark className="w-5 h-5" />,
      color: 'sky',
      hints: [
        "List leveraged loans by borrower",
        "Which loans are on watchlist or non-accrual?",
        "Show loans maturing soonest",
        "What's the weighted spread of the book?"
      ]
    },
    {
      id: 'clo-analyst',
      name: 'CLO Analyst',
      role: 'CLO',
      desc: 'CLO deals and tranches — WARF, WAS, WAL, diversity, CCC bucket, subordination, ratings.',
      endpoint: '/api/agents/clo/ask',
      icon: <Boxes className="w-5 h-5" />,
      color: 'indigo',
      hints: [
        "List our CLO deals and their WARF",
        "Show CLO tranches by seniority",
        "Which deals have the highest CCC bucket?",
        "Show defaulted par across deals"
      ]
    },
    {
      id: 'cmbs-analyst',
      name: 'CMBS Analyst',
      role: 'CMBS',
      desc: 'CMBS pools — balance, pool factor, delinquency, specially-serviced %, realized losses.',
      endpoint: '/api/agents/cmbs/ask',
      icon: <Building2 className="w-5 h-5" />,
      color: 'amber',
      hints: [
        "List our CMBS pools",
        "Which pools have the highest 90+ delinquency?",
        "Show specially-serviced percentages",
        "What are cumulative realized losses?"
      ]
    },
    {
      id: 'cds-analyst',
      name: 'CDS Analyst',
      role: 'CDS',
      desc: 'Single-name & index credit default swaps — protection direction, notional, spread, MTM.',
      endpoint: '/api/agents/cds/ask',
      icon: <Shield className="w-5 h-5" />,
      color: 'violet',
      hints: [
        "List our CDS contracts",
        "Where are we net buyers of protection?",
        "Show current spreads vs coupons",
        "What's our total CDS mark-to-market?"
      ]
    },
    {
      id: 'distressed-analyst',
      name: 'Distressed Analyst',
      role: 'Distressed',
      desc: 'Restructuring / bankruptcy situations — type, milestones, DIP, recovery, our exposure.',
      endpoint: '/api/agents/distressed/ask',
      icon: <TrendingDown className="w-5 h-5" />,
      color: 'rose',
      hints: [
        "List active distressed situations",
        "Which situations have upcoming plan-filing deadlines?",
        "Show our total distressed exposure",
        "Any DIP facilities we're involved in?"
      ]
    }
  ];

  // Institutional agents grouped by asset class (mirrors the Autonomous Agents tabs)
  const institutionalGroups: { id: string; label: string; icon: JSX.Element; agents: any[] }[] = [
    { id: 'portfolio',    label: 'Portfolio',      icon: <BarChart3 className="w-4 h-4" />, agents: institutionalAgents },
    { id: 'alternatives', label: 'Alternatives',   icon: <Building2 className="w-4 h-4" />, agents: alternativesAgents },
    { id: 'credit-loans', label: 'Credit & Loans', icon: <Briefcase className="w-4 h-4" />, agents: creditLoansAgents },
  ];

  // Institutional asset-class subtab (only used when !isRetail)
  const [classTab, setClassTab] = useState('portfolio');
  const currentInstitutionalAgents: any[] = institutionalGroups.find(g => g.id === classTab)?.agents ?? [];

  // Select agents based on tenant type, then (for institutional) asset-class subtab
  const agents: any[] = isRetail ? retailAgents : currentInstitutionalAgents;
  const defaultAgentId = isRetail ? 'advisor-copilot' : (institutionalGroups[0].agents[0]?.id ?? '');
  const [activeAgentId, setActiveAgentId] = useState(defaultAgentId);

  // Reset active agent when tenant type changes
  useEffect(() => {
    setActiveAgentId(defaultAgentId);
  }, [isRetail, defaultAgentId]);

  // When the institutional asset-class subtab changes, select that class's first agent
  useEffect(() => {
    if (!isRetail) {
      const activeAgentInCurrentClass = currentInstitutionalAgents.some(agent => agent.id === activeAgentId);
      if (!activeAgentInCurrentClass) {
        setActiveAgentId(currentInstitutionalAgents[0]?.id ?? '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classTab, activeAgentId]);

  const agentNameMap: Record<string, string> = {
    // Institutional agents
    'MasterOrchestratorAgent': 'Intent Analyst',
    'TradeResearchAgent': 'Research Agent',
    'ComplianceAgent': 'Compliance Agent',
    'ReconciliationAgent': 'Reconciliation Agent',
    // Alternatives agents
    'PrivateCreditAgent': 'Private Credit Analyst',
    'RealEstateAgent': 'Real Estate Analyst',
    'PrivateEquityAgent': 'Private Equity Analyst',
    // Credit & Loans agents
    'LeveragedLoanAgent': 'Leveraged Loan Analyst',
    'ABFAgent': 'ABF Facility Analyst',
    'CLOAgent': 'CLO Analyst',
    'CMBSAgent': 'CMBS Analyst',
    'CDSAgent': 'CDS Analyst',
    'DistressedAgent': 'Distressed Analyst',
    // Retail agents
    'AdvisorCopilotAgent': 'Advisor Copilot',
    'TaxAlphaAgent': 'Tax Alpha Agent',
    'K1AccountingAgent': 'K-1 Processor',
    'AltsExecutionAgent': 'Alts Executor',
    'GovernanceAgent': 'Compliance Guardian'
  };

  // May be undefined for an asset-class subtab with no agents yet (e.g. Credit & Loans)
  const activeAgent = useMemo(() => agents.find(a => a.id === activeAgentId) || agents[0], [activeAgentId, agents]);

  const collapsedAgentSections = isRetail
    ? [{ id: 'retail', label: 'RIA', agents: retailAgents }]
    : institutionalGroups;

  const handleAgentSelect = (agentId: string, groupId?: string) => {
    if (!isRetail && groupId) {
      setClassTab(groupId);
    }
    setActiveAgentId(agentId);
  };

  // Clear chat when switching agents
  useEffect(() => {
    setMessages([]);
    setChatInput('');
  }, [activeAgentId]);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !activeAgent) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setIsLoading(true);

    try {
      // Prior turns for multi-turn context (exclude errors; cap to last 10)
      const conversationHistory = messages
        .filter(m => !m.isError && (m.role === 'user' || m.role === 'assistant') && m.content)
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      // Call real backend agent API
      const response = await axios.post(activeAgent.endpoint || '/api/agents/ask', {
        message: input,
        context: {
          username: user?.username,
          role: user?.role,
          timestamp: new Date().toISOString(),
          conversationHistory
        }
      });
      
      const data = response.data;
      
      if (data.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.answer,
          toolsUsed: data.toolsUsed,
          iterations: data.iterations,
          agent: data.agent,
          structured: data.structured
        }]);
        // A draft_covenant_amendment call may have staged a new pending approval
        if (activeAgentId === 'leveraged-loan-analyst') fetchPendingAmendments();
      } else {
        throw new Error(data.error || 'Agent failed to respond');
      }
    } catch (error: any) {
      console.error('Agent communication error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `I encountered an error while processing your request: ${error.response?.data?.error || error.message || 'Unknown error'}. Please ensure the backend services and LLM providers are correctly configured.`,
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Covenant amendment approval queue (Leveraged Loan Analyst — act-on-your-behalf action)
  const fetchPendingAmendments = useCallback(async () => {
    try {
      const r = await axios.get('/api/agents/leveraged-loan/amendments/pending');
      setPendingAmendments(r.data?.pending || []);
    } catch {
      setPendingAmendments([]);
    }
  }, []);

  useEffect(() => {
    if (activeAgentId === 'leveraged-loan-analyst') fetchPendingAmendments();
    else setPendingAmendments([]);
  }, [activeAgentId, fetchPendingAmendments]);

  const handleAmendmentDecision = async (a: any, action: 'approve' | 'reject') => {
    try {
      setAmendmentBusy(a.id);
      let rationale = 'Approved via AI agent hub';
      if (action === 'reject') {
        rationale = window.prompt('Reason for rejecting this amendment?') || 'Rejected via AI agent hub';
      }
      await axios.post(`/api/agents/leveraged-loan/amendment/${a.id}/${action}`, { rationale });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: action === 'approve'
          ? `✓ **Approved** amendment #${a.id} — *${a.covenant_affected}* updated to **${a.proposed_threshold}**.`
          : `✕ **Rejected** amendment #${a.id} — *${a.covenant_affected}*.`
      }]);
      fetchPendingAmendments();
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', isError: true, content: `Failed to ${action} amendment #${a.id}.` }]);
    } finally {
      setAmendmentBusy(null);
    }
  };

  return (
    <Container>
      <Sidebar $collapsed={sidebarCollapsed}>
        <WorkspaceRailToggle
          collapsed={sidebarCollapsed}
          label="Agents"
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        {sidebarCollapsed ? (
          <CollapsedRail>
            {collapsedAgentSections.map(section => (
              <CollapsedRailSection key={section.id}>
                {section.agents.map(agent => (
                  <CollapsedRailItem
                    key={agent.id}
                    $active={activeAgentId === agent.id}
                    $tone="cyan"
                    aria-label={agent.name}
                    title={`${agent.name} - ${agent.role}`}
                    onClick={() => handleAgentSelect(agent.id, section.id)}
                  >
                    {agent.icon}
                  </CollapsedRailItem>
                ))}
              </CollapsedRailSection>
            ))}
          </CollapsedRail>
        ) : (
          <>
            <div className="p-6 border-b border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white tracking-tight">Agent Hub</h2>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Autonomous Intelligence</p>
                </div>
              </div>
            </div>

            {!isRetail && (
              <div className="px-4 pt-3">
                <div className="flex gap-1 bg-slate-900/60 border border-white/10 rounded-xl p-1">
                  {institutionalGroups.map(g => (
                    <button
                      key={g.id}
                      onClick={() => setClassTab(g.id)}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-bold transition-all",
                        classTab === g.id ? "bg-indigo-500/20 text-indigo-300" : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                      )}
                    >
                      {g.icon}<span>{g.label}</span>
                      {g.agents.length > 0 && (
                        <span className={cn(
                          "text-[9px] px-1 rounded-full font-bold",
                          classTab === g.id ? "bg-indigo-500/30 text-indigo-200" : "bg-slate-700 text-slate-400"
                        )}>{g.agents.length}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {agents.map((agent, index) => (
                <React.Fragment key={agent.id}>
                  {index === 1 && agents[0]?.isOrchestrator && (
                    <div className="flex items-center gap-3 px-4 pt-3 pb-1">
                      <div className="flex-1 h-px bg-white/5" />
                      <span className="text-[9px] font-bold text-slate-600 uppercase tracking-[0.15em]">Specialist Agents</span>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                  )}
                  <div
                    onClick={() => setActiveAgentId(agent.id)}
                    className={cn(
                      "p-4 rounded-2xl cursor-pointer transition-all border group",
                      activeAgentId === agent.id
                        ? "bg-indigo-500/10 border-indigo-500/30 shadow-inner"
                        : "bg-transparent border-transparent hover:bg-white/5"
                    )}
                  >
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-lg",
                        activeAgentId === agent.id ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-500 group-hover:bg-slate-700 group-hover:text-slate-300"
                      )}>
                        {agent.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className={cn("text-sm font-bold transition-colors", activeAgentId === agent.id ? "text-white" : "text-slate-400 group-hover:text-slate-200")}>
                            {agent.name}
                          </span>
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        </div>
                        <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-2">{agent.role}</div>
                        <p className="text-xs text-slate-500 leading-relaxed truncate">{agent.desc}</p>
                        {agent.isOrchestrator && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded-md">
                            <Zap className="w-3 h-3 text-blue-400" />
                            <span className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">Routes to all agents</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div className="p-4 border-t border-white/5 bg-slate-950/20 space-y-2">
              {isRetail && (
                <Link
                  to="/audit-trail"
                  className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600/20 to-violet-600/20 hover:from-indigo-600/30 hover:to-violet-600/30 border border-indigo-500/30 rounded-xl text-xs font-bold text-indigo-300 hover:text-white transition-all flex items-center justify-center gap-2 group"
                >
                  <ClipboardList className="w-4 h-4" />
                  Audit Trail
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              )}
              <button className="w-full py-3 px-4 bg-slate-900 hover:bg-slate-800 border border-white/5 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition-all flex items-center justify-center gap-2">
                <History className="w-4 h-4" />
                Conversation History
              </button>
            </div>
          </>
        )}
      </Sidebar>

      <ChatContainer>
        {!activeAgent ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
            <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-white/10 flex items-center justify-center mb-4">
              <Briefcase className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Credit &amp; Loans Agents</h3>
            <p className="text-slate-400 text-sm max-w-md leading-relaxed">
              Conversational agents for ABF, leveraged loans, CLO, CMBS, CDS and distressed credit are coming online here.
            </p>
          </div>
        ) : (
        <>
        {/* Chat Header */}
        <div className="h-20 border-b border-white/5 flex items-center justify-between px-8 bg-slate-950/20 backdrop-blur-md sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
              {activeAgent.icon}
            </div>
            <div>
              <div className="text-lg font-bold text-white tracking-tight">{activeAgent.name}</div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Active System</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="p-2 text-slate-500 hover:text-white transition-colors"><Settings className="w-5 h-5" /></button>
            <button className="p-2 text-slate-500 hover:text-white transition-colors"><MoreVertical className="w-5 h-5" /></button>
          </div>
        </div>

        {activeAgentId === 'leveraged-loan-analyst' && pendingAmendments.length > 0 && (
          <div className="px-8 pt-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-bold text-amber-300">Amendments Pending Your Approval ({pendingAmendments.length})</span>
              </div>
              <div className="space-y-2">
                {pendingAmendments.map((a: any) => (
                  <div key={a.id} className="bg-slate-900/60 border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-white font-medium truncate">#{a.id} · {a.borrower_name || a.loan_name}</div>
                      <div className="text-xs text-slate-400 truncate">
                        {a.covenant_affected} · {String(a.current_threshold)} → {String(a.proposed_threshold)}
                        {a.consent_deadline ? ` · deadline ${a.consent_deadline}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleAmendmentDecision(a, 'approve')} disabled={amendmentBusy === a.id}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-all disabled:opacity-50">
                        Approve
                      </button>
                      <button onClick={() => handleAmendmentDecision(a, 'reject')} disabled={amendmentBusy === a.id}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 transition-all disabled:opacity-50">
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-6">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-6">
              <div className="w-20 h-20 rounded-3xl bg-indigo-500/5 border border-indigo-500/10 flex items-center justify-center text-indigo-500/20">
                <Bot className="w-10 h-10" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Initialize {activeAgent.name}</h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto leading-relaxed">
                  Start a conversation to automate complex {activeAgent.role.toLowerCase()} workflows using institutional knowledge.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 max-w-lg">
                {(activeAgent.hints || []).map((hint: string) => (
                  <button key={hint} onClick={() => setChatInput(hint)} className="p-3 bg-slate-900 border border-white/5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:border-indigo-500/30 transition-all text-left">
                    "{hint}"
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((msg, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    "flex gap-4 max-w-3xl",
                    msg.role === 'user' ? "ml-auto flex-row-reverse" : "mr-auto"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-lg shrink-0 flex items-center justify-center",
                    msg.role === 'user' ? "bg-blue-600 text-white" : "bg-indigo-600/20 text-indigo-400 border border-indigo-500/20"
                  )}>
                    {msg.role === 'user' ? <User className="w-4 h-4" /> : activeAgent.icon}
                  </div>
                  <div className={cn(
                    "p-4 rounded-2xl text-sm leading-relaxed shadow-xl",
                    msg.role === 'user' 
                      ? "bg-blue-600 text-white rounded-tr-none"
                      : "bg-slate-900 border border-white/5 text-slate-200 rounded-tl-none"
                  )}>
                    {msg.role === 'assistant' && !msg.isError
                      ? <MarkdownBody dangerouslySetInnerHTML={{ __html: mdToHtml(msg.content) }} />
                      : msg.content}

                    {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                          <Cpu className="w-3 h-3" /> Tools Executed
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {msg.toolsUsed.map((tool: any, idx: number) => (
                            <div key={idx} className="px-2 py-1 bg-white/5 rounded-md text-[10px] font-medium text-slate-400 border border-white/5">
                              {tool.tool} ({tool.durationMs}ms)
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {msg.agent && activeAgent.isOrchestrator && msg.agent !== 'MasterOrchestratorAgent' && (
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 bg-violet-500/10 border border-violet-500/20 rounded-md">
                        <Layers className="w-3 h-3 text-violet-400" />
                        <span className="text-[10px] font-semibold text-violet-400">
                          Delegated to {agentNameMap[msg.agent] || msg.agent}
                        </span>
                      </div>
                    )}

                    {msg.iterations && (
                      <div className="mt-2 text-[10px] text-slate-600 italic">
                        Completed in {msg.iterations} {msg.iterations === 1 ? 'iteration' : 'iterations'}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {isLoading && (
            <div className="flex gap-4 max-w-3xl mr-auto">
              <div className="w-8 h-8 rounded-lg bg-indigo-600/20 text-indigo-400 border border-indigo-500/20 flex items-center justify-center">
                {activeAgent.icon}
              </div>
              <div className="bg-slate-900 border border-white/5 p-4 rounded-2xl rounded-tl-none">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <motion.div 
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-8 pt-0">
          <div className="max-w-4xl mx-auto relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-20 group-focus-within:opacity-40 transition-opacity" />
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="flex items-center px-4 py-2 border-b border-white/5 bg-slate-950/30">
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500/20" />
                  <div className="w-2 h-2 rounded-full bg-amber-500/20" />
                  <div className="w-2 h-2 rounded-full bg-emerald-500/20" />
                </div>
                <div className="mx-auto text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em]">Secure AI Session</div>
              </div>
              <div className="flex items-center p-2">
                <textarea 
                  rows={1}
                  value={input}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={`Send message to ${activeAgent.name}...`}
                  className="flex-1 bg-transparent border-none text-white text-sm p-4 focus:ring-0 resize-none placeholder:text-slate-600"
                />
                <button 
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  className="mr-2 p-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <p className="text-center mt-4 text-[10px] text-slate-600 font-medium">Axiom AI may provide inaccurate data. Always verify critical trade instructions.</p>
        </div>
        </>
        )}
      </ChatContainer>
    </Container>
  );
};

export default AIAgentsHub;
